import {
  AnnouncementTarget,
  AnnouncementType,
} from "@/store/api/endpoints/client/announcementsApi";

/**
 * Per-type chip metadata: PrimeIcons class, tinted background/text and i18n key.
 *
 * Mirrors the admin portal's `announcementTypeMeta` so look-and-feel stays
 * consistent across the two apps.
 */
export interface AnnouncementTypeMeta {
  icon: string;
  /** Tailwind classes for the chip pill (background + text). */
  chipClassName: string;
  /** Tailwind classes for the round icon avatar. */
  avatarClassName: string;
  /** Tailwind classes for the card surface (background + border). */
  cardClassName: string;
  /** Tailwind `from-*` class for the collapsed message fade gradient. */
  fadeFromClassName: string;
  /** i18n label key under the `announcements:type` namespace. */
  labelKey: string;
}

export const announcementTypeMeta: Record<
  Exclude<AnnouncementType, typeof AnnouncementType.Unknown>,
  AnnouncementTypeMeta
> = {
  [AnnouncementType.News]: {
    icon: "pi-megaphone",
    chipClassName: "bg-blue-100 text-blue-600",
    avatarClassName: "bg-blue-600 text-white",
    cardClassName: "border-blue-100 bg-blue-50",
    fadeFromClassName: "from-blue-50",
    labelKey: "type.News",
  },
  [AnnouncementType.Issue]: {
    icon: "pi-exclamation-triangle",
    chipClassName: "bg-red-100 text-red-600",
    avatarClassName: "bg-red-600 text-white",
    cardClassName: "border-red-100 bg-red-50",
    fadeFromClassName: "from-red-50",
    labelKey: "type.Issue",
  },
  [AnnouncementType.NewVersion]: {
    icon: "pi-sparkles",
    chipClassName: "bg-purple-100 text-purple-600",
    avatarClassName: "bg-purple-600 text-white",
    cardClassName: "border-purple-100 bg-purple-50",
    fadeFromClassName: "from-purple-50",
    labelKey: "type.NewVersion",
  },
  [AnnouncementType.Maintenance]: {
    icon: "pi-wrench",
    chipClassName: "bg-yellow-100 text-yellow-700",
    avatarClassName: "bg-yellow-700 text-white",
    cardClassName: "border-yellow-100 bg-yellow-50",
    fadeFromClassName: "from-yellow-50",
    labelKey: "type.Maintenance",
  },
};

/**
 * Per-target chip metadata. Targets are intentionally neutral — they describe
 * routing, not severity.
 */
export const announcementTargetMeta: Record<
  AnnouncementTarget,
  { icon: string; labelKey: string }
> = {
  [AnnouncementTarget.All]: { icon: "pi-globe", labelKey: "filters.appliesTo.All" },
  [AnnouncementTarget.Dashboard]: { icon: "pi-th-large", labelKey: "filters.appliesTo.Dashboard" },
  [AnnouncementTarget.Stock]: { icon: "pi-warehouse", labelKey: "filters.appliesTo.Stock" },
  [AnnouncementTarget.OutboundOrders]: {
    icon: "pi-send",
    labelKey: "filters.appliesTo.OutboundOrders",
  },
  [AnnouncementTarget.InboundOrders]: {
    icon: "pi-inbox",
    labelKey: "filters.appliesTo.InboundOrders",
  },
  [AnnouncementTarget.RoadTransport]: {
    icon: "pi-truck",
    labelKey: "filters.appliesTo.RoadTransport",
  },
  [AnnouncementTarget.Containers]: { icon: "pi-box", labelKey: "filters.appliesTo.Containers" },
  [AnnouncementTarget.AddressBook]: { icon: "pi-book", labelKey: "filters.appliesTo.AddressBook" },
  [AnnouncementTarget.Billing]: { icon: "pi-credit-card", labelKey: "filters.appliesTo.Billing" },
};

/**
 * Surfacing priority for the issue/maintenance banner.
 * Issues outrank maintenance; within the same type, `startDate desc` picks
 * the newest item (e.g. two issues → latest issue).
 */
export const ANNOUNCEMENT_TYPE_PRIORITY: Record<AnnouncementType, number> = {
  [AnnouncementType.Issue]: 0,
  [AnnouncementType.NewVersion]: 1,
  [AnnouncementType.News]: 2,
  [AnnouncementType.Maintenance]: 3,
  [AnnouncementType.Unknown]: 99,
};

export const VISIBLE_ANNOUNCEMENT_TYPES: Array<
  Exclude<AnnouncementType, typeof AnnouncementType.Unknown>
> = [
  AnnouncementType.Issue,
  AnnouncementType.NewVersion,
  AnnouncementType.News,
  AnnouncementType.Maintenance,
];

export const VISIBLE_ANNOUNCEMENT_TARGETS: AnnouncementTarget[] = [
  AnnouncementTarget.Dashboard,
  AnnouncementTarget.InboundOrders,
  AnnouncementTarget.Stock,
  AnnouncementTarget.OutboundOrders,
  AnnouncementTarget.Containers,
  AnnouncementTarget.RoadTransport,
  AnnouncementTarget.AddressBook,
  AnnouncementTarget.Billing,
];

export function normalizeAnnouncementTargets(
  targets: AnnouncementTarget[] | AnnouncementTarget | null | undefined,
  collapseAll = true,
): AnnouncementTarget[] {
  const normalized = Array.isArray(targets)
    ? targets
    : targets
      ? [targets]
      : [];
  return collapseAll && normalized.includes(AnnouncementTarget.All)
    ? [AnnouncementTarget.All]
    : normalized;
}

export function matchesAnnouncementTarget(
  targets: AnnouncementTarget[] | AnnouncementTarget | null | undefined,
  selectedTarget: AnnouncementTarget,
): boolean {
  const normalized = normalizeAnnouncementTargets(targets);
  return normalized.includes(AnnouncementTarget.All) || normalized.includes(selectedTarget);
}

/**
 * Sentinel for pages that have no dedicated AnnouncementTarget (e.g. Dashboard,
 * Settings, Profile). These pages receive 'All' announcements but are excluded
 * from announcements with a specific target (e.g. Stock-only).
 *
 * Not part of AnnouncementTarget — it is not selectable in the admin portal.
 */
export const IMPLICIT_PAGE_TARGET = "implicit" as const;
export type ImplicitPageTarget = typeof IMPLICIT_PAGE_TARGET;

/**
 * The page context passed to announcement pickers:
 * - AnnouncementTarget  — a named page (Stock, InboundOrders, etc.)
 * - 'implicit'          — a page with no dedicated target (Dashboard, Settings)
 * - undefined           — no filter (backwards-compatible call sites)
 */
export type AnnouncementPageContext = AnnouncementTarget | ImplicitPageTarget;

/**
 * Core visibility rule for whether an announcement belongs on a given page.
 * Centralised here so future rule changes require a single edit.
 *
 * | pageContext  | appliesTo ['All'] | appliesTo ['Stock'] |
 * |-------------|-------------------|---------------------|
 * | undefined   | ✓ (no filter)     | ✓ (no filter)       |
 * | 'implicit'  | ✓                 | ✗                   |
 * | 'Stock'     | ✓                 | ✓                   |
 * | 'Inbound'   | ✓                 | ✗                   |
 */
export function announcementMatchesPageContext(
  appliesTo: AnnouncementTarget[],
  pageContext: AnnouncementPageContext | undefined,
): boolean {
  if (pageContext === undefined) return true;
  if (appliesTo.includes(AnnouncementTarget.All)) return true;
  if (pageContext === IMPLICIT_PAGE_TARGET) return false;
  return appliesTo.includes(pageContext);
}

const PATH_SEGMENT_TO_TARGET: Record<string, AnnouncementTarget> = {
  inbound: AnnouncementTarget.InboundOrders,
  stock: AnnouncementTarget.Stock,
  outbound: AnnouncementTarget.OutboundOrders,
  "road-transport": AnnouncementTarget.RoadTransport,
  containers: AnnouncementTarget.Containers,
  "address-book": AnnouncementTarget.AddressBook,
  billing: AnnouncementTarget.Billing,
};

/**
 * Maps the current URL pathname to an AnnouncementPageContext.
 * Handles both non-prefixed (/inbound) and tenant-prefixed (/:tenant/inbound) routes.
 * Returns IMPLICIT_PAGE_TARGET for pages with no matching target (Dashboard, Settings, etc.).
 */
export function getAnnouncementTargetForPath(
  pathname: string,
): AnnouncementPageContext {
  const segments = pathname.split("/").filter(Boolean);
  for (const segment of segments) {
    const target = PATH_SEGMENT_TO_TARGET[segment];
    if (target) return target;
  }
  return IMPLICIT_PAGE_TARGET;
}

/** Resolved chip data for each known target on an announcement card. */
export function getAnnouncementTargetChips(
  targets: AnnouncementTarget[] | AnnouncementTarget | null | undefined,
) {
  return Array.from(new Set(normalizeAnnouncementTargets(targets, false)))
    .map((target) => {
      const meta = announcementTargetMeta[target];
      return meta ? { key: target, ...meta } : null;
    })
    .filter((chip): chip is NonNullable<typeof chip> => chip !== null);
}
